import { loadSettings, saveSettings } from '@/config/localSettings';
import {
  DEFAULT_SETTINGS,
  loadSessionSettings,
  saveSessionSettings,
} from '@/config/sessionSettings';
import type { SelectedMenus } from '@/types/menu.type';
import type { Organization } from '@/types/organization';
import { useUserStore } from './userStore';

import { create } from 'zustand';

interface AppState {
  loading: boolean;
  lnbExpanded: boolean;
  firstSelectedMenu: string;
  selectedMenus: SelectedMenus;
  selectedOrg: Organization | null;
  secondMenuTitle: Record<string, string>;
  initializeSessionSettings: () => void;
  setLoading: (loading: boolean) => void;
  setLnbExpanded: (expanded: boolean) => void;
  setFirstSelectedMenu: (key: string) => void;
  setSelectedMenus: (selectedMenus: SelectedMenus, key: string) => void;
  setSelectedOrg: (organization: Organization | null) => void;
  setSecondMenuTitle: (title: Record<string, string>) => void;
}

const getUserId = () => {
  try {
    const { user } = useUserStore.getState();
    return user?.id;
  } catch {
    return undefined;
  }
};

export const useAppStore = create<AppState>((set, get) => {
  const savedSettings = loadSettings();

  return {
    loading: false,
    lnbExpanded: savedSettings.lnbExpanded,
    firstSelectedMenu: DEFAULT_SETTINGS.firstSelectedMenu,
    selectedMenus: DEFAULT_SETTINGS.selectedMenus,
    selectedOrg: DEFAULT_SETTINGS.selectedOrg,
    secondMenuTitle: DEFAULT_SETTINGS.secondMenuTitle,
    setLoading: (loading) => {
      set({ loading });
    },
    setFirstSelectedMenu: (firstSelectedMenu) => {
      set({ firstSelectedMenu });
      saveSessionSettings({ firstSelectedMenu }, getUserId());
    },
    setSelectedMenus: (selectedMenus: SelectedMenus, key: string) => {
      const newSelectedMenus = {
        ...get().selectedMenus,
        [key]: selectedMenus[key],
      };
      set({ selectedMenus: newSelectedMenus });
      saveSessionSettings({ selectedMenus: newSelectedMenus }, getUserId());
    },
    setLnbExpanded: (expanded) => {
      set({ lnbExpanded: expanded });
      saveSettings({ lnbExpanded: expanded });
    },
    setSelectedOrg: (organization: Organization | null) => {
      set({ selectedOrg: organization });
      saveSessionSettings({ selectedOrg: organization }, getUserId());
    },
    setSecondMenuTitle: (title) => {
      set({ secondMenuTitle: title });
      saveSessionSettings({ secondMenuTitle: title }, getUserId());
    },
    initializeSessionSettings: () => {
      const userId = getUserId();

      if (!userId) {
        console.warn('initializeSessionSettings: userId is empty');
        return;
      }

      const savedSessionSettings = loadSessionSettings(userId);

      set({
        firstSelectedMenu: savedSessionSettings.firstSelectedMenu,
        selectedMenus: savedSessionSettings.selectedMenus,
        selectedOrg: savedSessionSettings.selectedOrg,
        secondMenuTitle: savedSessionSettings.secondMenuTitle,
      });
    },
  };
});
